import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { PasswordInput } from '../components/PasswordInput';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success(language === 'fr' ? 'Connexion réussie !' : 'Login successful!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || t('invalidCredentials');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 hero-bg">
      <div className="w-full max-w-md">
        <div className="glass-card rounded-3xl p-8 sm:p-10 shadow-xl animate-scale-in">
          {/* Header */}
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_stop-tabac-2/artifacts/u4es5x0d_Application.jpeg"
              alt="ZéphyrBreath"
              className="w-16 h-16 rounded-2xl object-cover mx-auto mb-4"
            />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {language === 'fr' ? 'Bon retour !' : 'Welcome back!'}
            </h1>
            <p className="text-gray-600">
              {language === 'fr' 
                ? 'Connectez-vous pour accéder à votre espace'
                : 'Log in to access your dashboard'}
            </p>
          </div>

          {/* Google Sign-in */}
          <GoogleSignInButton testId="google-signin-login-btn" />

          {/* Divider */}
          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 uppercase tracking-wider">
              {language === 'fr' ? 'ou' : 'or'}
            </span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 font-medium">
                {t('email')}
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="pl-12 h-12 rounded-xl border-gray-200 bg-white/50 focus:ring-2 focus:ring-primary/20"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700 font-medium">
                {t('password')}
              </Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                testId="login-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all text-lg font-semibold"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  {t('loginToAccount')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>

            <div className="text-center">
              <Link 
                to="/forgot-password" 
                className="text-sm text-gray-500 hover:text-primary transition-colors"
                data-testid="forgot-password-link"
              >
                {language === 'fr' ? 'Mot de passe oublié ?' : 'Forgot password?'}
              </Link>
            </div>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-gray-600">
              {t('noAccount')}{' '}
              <Link 
                to="/register" 
                className="text-primary font-semibold hover:underline"
                data-testid="register-link"
              >
                {t('registerHere')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
